import React, { useState, useEffect, useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import ContactModal from "../common/ContactModal";
import PrimaryButton from "../common/PrimaryButton";
import { homeData } from "../../data/homeData";
import { assetPath } from "../../utils/assetPath";

/* ── Cinematic animated background with parallax ── */
const HeroBackground = ({ scrollYProgress }) => {
  const bgOpacity = useTransform(scrollYProgress, [0, 0.8], [1, 1]);
  const scale = useTransform(scrollYProgress, [0, 1], [1.05, 1.25]);
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);

  return (
    <div className="absolute inset-0 z-0 overflow-hidden pointer-events-none">
      {/* Smooth Parallax Background — GPU composited */}
      <motion.img
        src={assetPath("/HomePage/heroBG.webp")}
        alt="Hero Background"
        aria-hidden="true"
        style={{ scale, y, opacity: bgOpacity, translateZ: 0 }}
        className="absolute inset-0 w-full h-[120%] -top-[15%] object-cover object-[center_30%] will-change-transform"
      />

      {/* Black Overlay for text legibility */}
      <div className="absolute inset-0 bg-black/20 z-1" />

      {/* Top dark fade for header blending */}
      <div className="absolute top-0 left-0 right-0 h-40 pointer-events-none" />
    </div>
  );
};

/* ── Main Hero Component ── */
const Hero = () => {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });

  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const { hero, stats } = homeData;

  useEffect(() => {
    // Trigger entrance animations after mount
    const timer = setTimeout(() => setIsLoaded(true), 100);
    return () => clearTimeout(timer);
  }, []);

  const headingLine1 = hero.headingText.split("\n")[0];
  const headingLine2 = hero.headingText.split("\n")[1];

  const trustPartners = [
    "Etihad Airways",
    "IndiGo",
    "CAE",
    "Alstom",
    "Indian Navy",
  ];

  return (
    <>
      <section
        ref={heroRef}
        className="relative w-full min-h-[220vh] flex flex-col justify-start overflow-hidden pointer-events-none"
      >
        {/* Cinematic Background */}
        <HeroBackground scrollYProgress={scrollYProgress} />

        {/* Content Viewports Wrapper */}
        <div className="relative z-10 w-full">
          {/* Viewport 1: Initial content */}
          <div className="w-full h-screen flex flex-col justify-end pb-10 md:pb-15 px-6 max-w-[1400px] mx-auto pt-32 text-left items-start">
            {/* Heading  Split into two dramatic lines */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={isLoaded ? { opacity: 1 } : {}}
              transition={{ duration: 0.3, delay: 0.3 }}
              className="mb-8 w-full max-w-[900px]"
            >
              {/* Line 1 */}
              <motion.div
                initial={{ opacity: 0, y: 40 }}
                animate={isLoaded ? { opacity: 1, y: 0 } : {}}
                transition={{
                  duration: 1,
                  delay: 0.4,
                  ease: [0.22, 1, 0.36, 1],
                }}
                className="will-change-[transform,opacity]"
              >
                <h1 className="text-white tracking-tighter text-[24px] sm:text-[30px] md:text-[36px] lg:text-[46px] leading-[1.2] font-bold">
                  {headingLine1}
                </h1>
              </motion.div>

              {/* Line 2 */}
              <motion.div
                initial={{ opacity: 0, y: 40 }}
                animate={isLoaded ? { opacity: 1, y: 0 } : {}}
                transition={{
                  duration: 1,
                  delay: 0.6,
                  ease: [0.22, 1, 0.36, 1],
                }}
                className="mt-1 will-change-[transform,opacity]"
              >
                <h2 className="text-white/95 tracking-tighter text-[24px] sm:text-[30px] md:text-[36px] lg:text-[46px] leading-[1.2] font-bold">
                  {headingLine2}
                </h2>
              </motion.div>
            </motion.div>

            {/* AI Badge  moved below heading based on typical clean layout or text order */}
            <motion.div
              initial={{ opacity: 0, y: -20, scale: 0.95 }}
              animate={isLoaded ? { opacity: 1, y: 0, scale: 1 } : {}}
              transition={{
                duration: 0.8,
                delay: 0.8,
                ease: [0.22, 1, 0.36, 1],
              }}
              className="flex flex-col gap-5 items-start mb-14"
            >
              <div className="font-bold text-white tracking-wide text-[14px] md:text-[15px] opacity-90">
                {hero.subBadge}
              </div>

              {/* Trusted By Chips */}
              <div className="flex flex-wrap gap-2.5 items-center">
                <span className="text-white/90 text-[11px] font-bold tracking-widest uppercase mr-3">
                  Trusted by
                </span>
                {trustPartners.map((partner, i) => (
                  <motion.span
                    key={partner}
                    initial={{ opacity: 0, y: 10, scale: 0.8 }}
                    animate={isLoaded ? { opacity: 1, y: 0, scale: 1 } : {}}
                    transition={{
                      type: "spring",
                      stiffness: 260,
                      damping: 20,
                      delay: 1.1 + i * 0.1,
                    }}
                    whileHover={{
                      scale: 1.05,
                      backgroundColor: "rgba(255, 255, 255, 0.12)",
                      borderColor: "rgba(255, 255, 255, 0.3)",
                      boxShadow: "0 0 20px rgba(255, 255, 255, 0.05)",
                    }}
                    className="px-5 py-2 rounded-full bg-white/5 border border-white/15 text-white md:text-[13px] text-[12px] font-semibold backdrop-blur-md transition-shadow cursor-default flex items-center gap-2 group shadow-sm"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-white/40 group-hover:bg-white transition-colors" />
                    {partner}
                  </motion.span>
                ))}
              </div>
            </motion.div>

            {/* End of Viewport 1 */}
          </div>

          {/* Viewport 2: Content appears sequentially in natural scroll flow */}
          <div className="w-full min-h-[80vh] md:min-h-[90vh] flex flex-col justify-start pt-12 md:pt-20 px-6 max-w-[1400px] mx-auto pointer-events-auto items-start">
            {/* Description */}
            <p className="text-white text-base sm:text-lg lg:text-[1.2rem] leading-[1.6] max-w-[650px] mb-12 font-medium drop-shadow-md">
              {hero.description}
            </p>

            {/* CTAs  Dramatic entrance */}
            <div className="flex flex-col items-start gap-4 mb-20 pointer-events-auto">
              <div className="flex flex-col sm:flex-row items-center gap-4 md:gap-6">
                <a
                  href="#platforms"
                  className="group w-full sm:w-auto inline-flex items-center justify-center text-center gap-2.5 
                             px-7 py-3.5 rounded-full 
                             border border-white/8 bg-white/4 backdrop-blur-xl
                             text-white/80 text-sm font-bold tracking-tight 
                             transition-all duration-500 
                             hover:bg-white/10 hover:border-white/30 hover:text-white
                             hover:shadow-[0_0_25px_rgba(59,130,246,0.15)]"
                >
                  ✦ See What We've Built
                </a>
                <PrimaryButton
                  onClick={() => setIsContactModalOpen(true)}
                  className="w-full sm:w-auto shadow-[0_0_30px_rgba(255,255,255,0.1)] py-3.5 bg-[#0084F9]! rounded-full font-bold flex items-center justify-center gap-2 transition-all px-8 !text-white relative flex-row !h-auto focus:outline-hidden border-none cursor-pointer group"
                  withoutSpan={true}
                >
                  {hero.primaryCTA}{" "}
                </PrimaryButton>
              </div>
              {/* Viewport 2 Microcopy */}
              <p className="text-white text-[12px] md:text-[12px] font-medium tracking-wide max-w-[600px] leading-relaxed pl-2">
                {hero.microcopy}
              </p>
            </div>
          </div>
        </div>
      </section>

      <ContactModal
        isOpen={isContactModalOpen}
        onClose={() => setIsContactModalOpen(false)}
      />
    </>
  );
};

export default Hero;
